import React, { useState, useEffect } from 'react';
import { useJazyk } from '../context/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LotusLogo } from '../components/LotusLogo';
import { LoginForm } from '../components/auth/LoginForm';
import { RegisterForm } from '../components/auth/RegisterForm';
import BugReportModal from '../components/BugReportModal';
import { Smartphone, Bug, ShieldCheck } from 'lucide-react';

import { API } from '../lib/api';
import { useLocation } from 'react-router-dom';

// Re-export for backward compat (not used elsewhere but keep safe)
export { API };

const AuthPage = () => {
  const { t } = useJazyk();
  const [loading, setLoading] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [stats, setStats] = useState(null);
  // ?tab=register otevře rovnou registraci – používá to tlačítko na úvodní stránce
  const { search } = useLocation();
  const initialTab = new URLSearchParams(search).get('tab') === 'register' ? 'register' : 'login';

  useEffect(() => {
    axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-8 px-4 bg-bloom-bg" data-testid="auth-page">
      <div className="pride-bar fixed top-0 left-0 right-0 z-50" />

      <Card className="w-full max-w-lg bg-white border-border/50 shadow-lg relative z-10">
        <CardHeader className="text-center pb-4">
          <div className="flex flex-col items-center gap-0">
            <LotusLogo size={72} />
            <CardTitle className="font-serif text-3xl text-bloom-violet">Bloom</CardTitle>
          </div>
          {stats && (
            <div className="flex justify-center gap-4 pt-2 pb-1" data-testid="auth-stats">
              {[
                { emoji: '🌸', value: stats.members, label: t('auth.clenu', { pocet: stats.members }) },
                { emoji: '💬', value: stats.experiences, label: t('auth.prispevku', { pocet: stats.experiences }) },
                { emoji: '🧠', value: stats.specialists, label: t('auth.odborniku', { pocet: stats.specialists }) },
                { emoji: '🤝', value: stats.services, label: t('auth.nabidek', { pocet: stats.services }) },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <div className="text-sm">{s.emoji}</div>
                  <div className="text-sm font-bold text-bloom-text">{s.value}</div>
                  <div className="text-[10px] text-bloom-sub">{s.label}</div>
                </div>
              ))}
            </div>
          )}
          <CardDescription className="text-bloom-sub">{t('auth.podtitulek')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={initialTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-5 bg-muted">
              <TabsTrigger value="login" data-testid="login-tab">{t('auth.prihlaseni')}</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">{t('auth.registrace')}</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <LoginForm loading={loading} setLoading={setLoading} />
            </TabsContent>
            <TabsContent value="register">
              <RegisterForm loading={loading} setLoading={setLoading} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* PWA Install Instructions */}
      <div className="w-full max-w-lg mt-4 relative z-10">
        <details className="bg-white border border-border/50 rounded-xl shadow-sm">
          <summary className="px-4 py-3 text-sm font-medium text-bloom-sub cursor-pointer hover:text-bloom-violet transition-colors flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-bloom-violet shrink-0" />
            <span>{t('auth.instalaceTitulek')}</span>
          </summary>
          <div className="px-4 pb-4 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-bloom-text mb-1.5">{t('auth.instalaceAndroid')} (Chrome)</h4>
              <ol className="text-xs text-bloom-sub space-y-1 list-decimal list-inside">
                {t('auth.krokyAndroid').map((k) => <li key={k}>{k}</li>)}
              </ol>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-bloom-text mb-1.5">{t('auth.instalaceIos')} (Safari)</h4>
              <ol className="text-xs text-bloom-sub space-y-1 list-decimal list-inside">
                {t('auth.krokyIos').map((k) => <li key={k}>{k}</li>)}
              </ol>
            </div>
            <p className="text-xs text-bloom-sub/70">{t('auth.instalacePoznamka')}</p>
          </div>
        </details>


        {/* Zabezpečení – transparentně, ať si to každý může ověřit */}
        <details className="mt-3 bg-white border border-border/50 rounded-xl shadow-sm" data-testid="security-info">
          <summary className="px-4 py-3 text-sm font-medium text-bloom-sub cursor-pointer hover:text-bloom-violet transition-colors flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-bloom-mint shrink-0" />
            <span>{t('auth.zabezpeceniTitulek')}</span>
          </summary>
          <div className="px-4 pb-4 space-y-3">
            <p className="text-xs text-bloom-sub leading-relaxed">
              {t('auth.zabezpeceniUvod')}
            </p>

            <div>
              <h4 className="text-xs font-semibold text-bloom-text mb-1.5">{t('auth.coJeChranene')}</h4>
              <ul className="text-xs text-bloom-sub space-y-1 list-disc list-inside leading-relaxed">
                {t('auth.seznamChranene').map((o) => (
                  <li key={o.text}>
                    {o.silne && <strong>{o.silne}</strong>}{o.silne ? ' ' : ''}{o.text}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-bloom-text mb-1.5">{t('auth.coNeumime')}</h4>
              <ul className="text-xs text-bloom-sub space-y-1 list-disc list-inside leading-relaxed">
                {t('auth.seznamNeumime').map((o) => (
                  <li key={o.text}>
                    {o.silne && <strong>{o.silne}</strong>}{o.silne ? ' ' : ''}{o.text}
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-bloom-sub/70 leading-relaxed">
              {t('auth.zabezpeceniZaver')}
            </p>
          </div>
        </details>

        {/* MVP notice + feedback */}
        <div className="mt-3 bg-white border border-border/50 rounded-xl shadow-sm px-4 py-3 space-y-2">
          <p className="text-xs text-bloom-sub leading-relaxed">
            {t('auth.mvp')}
          </p>
          <button
            onClick={() => setBugReportOpen(true)}
            className="flex items-center gap-1.5 text-xs text-bloom-violet hover:text-bloom-violet/80 transition-colors font-medium"
            data-testid="auth-bug-report-btn"
          >
            <Bug className="w-3.5 h-3.5" />{t('auth.nahlasitProblem')}
          </button>
        </div>
      </div>

      <BugReportModal open={bugReportOpen} onClose={() => setBugReportOpen(false)} />
    </div>
  );
};

export default AuthPage;
